import { useEffect, useMemo, useRef, useState} from "react";
import { FiCalendar, FiMessageSquare } from "react-icons/fi";
import { LuX } from "react-icons/lu";
import useAppDataStore from "../../../../states/useAppDataStore";
import useDealStore from "../../../states/useDealStore";
import { Input, Modal } from "@iq/models";
import {
	addDealProduct,
	deleteDeal,
	deleteDealProduct,
	getDealProducts,
	getDealStageHistory,
	getContactByID,
	updateDeal,
} from "../../../services/contact_service";
import CreateMeetingModal from "../CreateMeetingModal";
import DealConversationsModal from "./DealConversationsModal";
import "../Modal.css";
import { getLostReasonLabel } from "../../../models/dealClosureModel";
import { ACQUISITION_SOURCES, getAcquisitionSourceLabel, getPrimaryOfferLabel, PRIMARY_OFFERS } from "../../../models/opportunityModel";
const CURRENCY_OPTIONS = [
	{ value: "USD", label: "USD" },
	{ value: "EUR", label: "EUR" },
	{ value: "GBP", label: "GBP" },
	{ value: "CAD", label: "CAD" },
];

function DealDetailsModal({ dealId, onClose, initialDeal = null, onDealUpdated, onDealDeleted }) {
	const deals = useDealStore((state) => state.deals);
	const availableProducts = useAppDataStore((state) => state.products);
	const requestContactNav = useAppDataStore((state) => state.requestContactNav)
	const pickerLoading = useAppDataStore((state) => state.productsLoading);
	const pickerError = useAppDataStore((state) => state.productsError);
	const [dealProducts, setDealProducts] = useState([]);
	const [productsLoading, setProductsLoading] = useState(false);
	const [productsError, setProductsError] = useState("");
	const [stageHistory, setStageHistory] = useState([]);
	const [stageHistoryLoading, setStageHistoryLoading] = useState(false);
	const [stageHistoryError, setStageHistoryError] = useState("");
	const [productQuery, setProductQuery] = useState("");
	const [isCreateMeetingOpen, setIsCreateMeetingOpen] = useState(false);
	const [isConversationsOpen, setIsConversationsOpen] = useState(false);
	const [editForm, setEditForm] = useState({});
	const [isSaving, setIsSaving] = useState(false);
	const [saveError, setSaveError] = useState("");
	const [addProductError, setAddProductError] = useState("");
	const [isAddingProduct, setIsAddingProduct] = useState(false);
	const [removingProductId, setRemovingProductId] = useState(null);
	const [contactLoading, setContactLoading] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const lastSavedPayloadRef = useRef("");

	async function handleContactNameClick() {
		if(!deal?.contact_id || contactLoading){
			return;
		}
		setContactLoading(true);
		

		try{
			const contactData = await getContactByID(deal.contact_id);
			requestContactNav(contactData);
			onClose?.();
		} catch (err){
			console.error("Failed to load new contact: ", err);
		} finally{
			setContactLoading(false);
		}
		
	}

	const deal = useMemo(
		() => deals.find((item) => Number(item.id) === Number(dealId)) || initialDeal,
		[deals, dealId, initialDeal]
	);



	const ownerLabel = deal?.owner_name || (deal?.owner ? `User #${deal.owner}` : "-");

	const productIdsInDeal = useMemo(
		() => new Set(dealProducts.map((item) => Number(item.product_id))),
		[dealProducts]
	);

	const selectableProducts = useMemo(
		() => availableProducts.filter((item) => !productIdsInDeal.has(Number(item.id))),
		[availableProducts, productIdsInDeal]
	);

	const selectedCurrencyLabel = useMemo(
		() => CURRENCY_OPTIONS.find((option) => option.value === (editForm.currency || "USD"))?.label || "USD",
		[editForm.currency]
	);

	const normalizedProductQuery = productQuery.trim().toLowerCase();
	const filteredSelectableProducts = useMemo(() => {
		if (!normalizedProductQuery) {
			return selectableProducts;
		}

		return selectableProducts.filter((product) => {
			const name = String(product.name || "").toLowerCase();
			const description = String(product.description || product.product_description || "").toLowerCase();
			return name.includes(normalizedProductQuery) || description.includes(normalizedProductQuery);
		});
	}, [selectableProducts, normalizedProductQuery]);

	const isChildModalOpen = isCreateMeetingOpen || isConversationsOpen;
	const savePayload = useMemo(() => ({
		title: editForm.title || "",
		deal_type: editForm.deal_type || "",
		acquisition_source: editForm.acquisition_source || "unknown",
		primary_offer: editForm.primary_offer || "unknown",
		amount_cents: Number(editForm.amount_cents || 0),
		currency: editForm.currency || "USD",
		probability: Number(deal?.probability || 0),
	}), [deal?.probability, editForm]);
	const serializedPayload = useMemo(() => JSON.stringify(savePayload), [savePayload]);
	const hasUnsavedChanges = Boolean(deal) && serializedPayload !== lastSavedPayloadRef.current;

	useEffect(() => {
		if (!deal) {
			setEditForm({
				title: "",
				deal_type: "",
				acquisition_source: "unknown",
				primary_offer: "unknown",
				amount_cents: 0,
				currency: "USD",
			});
			lastSavedPayloadRef.current = "";
			return;
		}

		setEditForm({
			title: deal.title || "",
			deal_type: deal.deal_type || "",
			acquisition_source: deal.acquisition_source || "unknown",
			primary_offer: deal.primary_offer || "unknown",
			amount_cents: deal.amount_cents || 0,
			currency: deal.currency || "USD",
		});
		setProductQuery("");
		lastSavedPayloadRef.current = JSON.stringify({
			title: deal.title || "",
			deal_type: deal.deal_type || "",
			acquisition_source: deal.acquisition_source || "unknown",
			primary_offer: deal.primary_offer || "unknown",
			amount_cents: Number(deal.amount_cents || 0),
			currency: deal.currency || "USD",
			probability: Number(deal.probability || 0),
		});
	}, [deal]);

	useEffect(() => {
		if (!dealId) {
			setDealProducts([]);
			setProductsLoading(false);
			setProductsError("");
			return;
		}

		let cancelled = false;
		setProductsLoading(true);
		setProductsError("");

		getDealProducts(dealId)
			.then((products) => {
				if (!cancelled) {
					setDealProducts(Array.isArray(products) ? products : []);
				}
			})
			.catch((err) => {
				if (!cancelled) {
					setDealProducts([]);
					setProductsError(err?.message || "Failed to load products");
				}
			})
			.finally(() => {
				if (!cancelled) {
					setProductsLoading(false);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [dealId]);

	useEffect(() => {
		if (!dealId) return;
		let cancelled = false;
		setStageHistoryLoading(true);
		setStageHistoryError("");
		getDealStageHistory(dealId)
			.then((rows) => {
				if (!cancelled) setStageHistory(Array.isArray(rows) ? rows : []);
			})
			.catch((err) => {
				if (!cancelled) {
					setStageHistory([]);
					setStageHistoryError(err?.message || "Failed to load stage history");
				}
			})
			.finally(() => {
				if (!cancelled) setStageHistoryLoading(false);
			});
		return () => { cancelled = true; };
	}, [dealId]);

	useEffect(() => {
		if (!dealId) {
			return;
		}

		function handleEscape(event) {
			if (event.key === "Escape") {
				event.preventDefault();
				onClose?.();
			}
		}

		window.addEventListener("keydown", handleEscape);
		return () => {
			window.removeEventListener("keydown", handleEscape);
		};
	}, [dealId, onClose]);

	if (!dealId) {
		return null;
	}

	async function handleSelectProduct(product) {
		if (!product?.id || isAddingProduct) {
			return;
		}

		setAddProductError("");
		setIsAddingProduct(true);
		try {
			const created = await addDealProduct(dealId, {
				productId: Number(product.id),
				descriptionOverride: "",
			});
			setDealProducts((prev) => [...prev, created]);
			setProductQuery("");
		} catch (err) {
			setAddProductError(err?.message || "Failed to add product");
		} finally {
			setIsAddingProduct(false);
		}
	}

	async function handleDeleteDeal() {
		if (!dealId || isDeleting) {
			return;
		}

		const confirmed = window.confirm("Delete this deal? This action cannot be undone.");
		if (!confirmed) {
			return;
		}

		setSaveError("");
		setIsDeleting(true);
		try {
			await deleteDeal(dealId);
			const removeDealFromStore = useDealStore.getState().removeDealFromStore;
			if (removeDealFromStore) {
				removeDealFromStore(dealId);
			}
			onDealDeleted?.(dealId);
			onClose?.();
		} catch (err) {
			setSaveError(err?.message || "Failed to delete deal");
		} finally {
			setIsDeleting(false);
		}
	}

	async function handleSaveChanges() {
		if (!dealId || !deal || isSaving || !hasUnsavedChanges) {
			return;
		}

		setSaveError("");
		setIsSaving(true);
		try {
			const updated = await updateDeal(dealId, savePayload);
			const updateDealInStore = useDealStore.getState().updateDealInStore;
			const mergedDeal = {
				...deal,
				...updated,
				...savePayload,
				id: updated?.id ?? deal?.id ?? dealId,
			};
			if (updateDealInStore) {
				updateDealInStore(mergedDeal);
			}
			onDealUpdated?.(mergedDeal);
			lastSavedPayloadRef.current = serializedPayload;
		} catch (err) {
			setSaveError(err?.message || "Failed to save deal");
		} finally {
			setIsSaving(false);
		}
	}

	async function handleRemoveProduct(product) {
		if (dealProducts.length < 2 || !product?.id || removingProductId) {
			return;
		}

		setAddProductError("");
		setRemovingProductId(product.id);
		try {
			await deleteDealProduct(dealId, product.id);
			setDealProducts((prev) => prev.filter((item) => Number(item.id) !== Number(product.id)));
		} catch (err) {
			console.error("handleRemoveProduct failed", {
				dealId,
				dealProductId: product?.id,
				productId: product?.product_id,
				product,
				error: err,
			});
			setAddProductError(err?.message || "Failed to remove product");
		} finally {
			setRemovingProductId(null);
		}
	}

	const actionButtons = [
		{
			key: "delete-deal",
			text: isDeleting ? "Deleting..." : "Delete Deal",
			color: "danger",
			onClick: handleDeleteDeal,
			disabled: isDeleting,
		},
		{
			key: "save-changes",
			text: isSaving ? "Saving..." : "Save Changes",
			color: "accent",
			onClick: handleSaveChanges,
			disabled: isDeleting || isSaving || !hasUnsavedChanges,
		},

	];

	return (
		<>
			{!isChildModalOpen && (
				<Modal
					isOpen={true}
					onClose={onClose}
					title="View Deal"
					actionButtons={actionButtons}
					errorMessage={saveError}
					shrink={true}
				>
			<div className="modal-form-container">
				{isSaving ? <span className="modal-form-field-note">Saving changes...</span> : null}
				<div className="form-fields-container">
					<div className="deal-header-container">
						<div className="deal-header-account">{deal?.account || "-"}</div>
						<div className="deal-header-contact"><span onClick={handleContactNameClick}>Contact: {deal?.contact_name || "-"}</span></div>
						<div className="deal-header-owner"><span>Owner: </span>{ownerLabel}</div>
						<div className="deal-header-actions">
							<div className="deal-header-action-btn-wrapper">
								<button
									type="button"
									className="deal-header-action-btn"
									onClick={() => setIsCreateMeetingOpen(true)}
									disabled={isDeleting || isSaving}
									aria-label="Create meeting"
								>
									<FiCalendar />
								</button>
								<span className="deal-header-action-label">Schedule</span>
							</div>
							<div className="deal-header-action-btn-wrapper">
								<button
									type="button"
									className="deal-header-action-btn"
									onClick={() => setIsConversationsOpen(true)}
									disabled={isDeleting || isSaving}
									aria-label="Open conversations"
								>
									<FiMessageSquare />
								</button>
								<span className="deal-header-action-label">Conversations</span>
							</div>
						</div>
					</div>

					{deal?.status === "closed" ? (
						<>
							<div className="modal-form-field">
								<label>Closed</label>
								<div className="form-static-value">{deal?.close_date ? new Date(deal.close_date).toLocaleDateString() : "-"}</div>
							</div>
							<div className="modal-form-field">
								<label>Outcome</label>
								<div className="form-static-value">{deal?.stage === "lost" ? getLostReasonLabel(deal?.lost_reason) || "Lost" : "Won"}</div>
							</div>
						</>
					) : null}

					<div className="modal-form-field">
						<label htmlFor="deal-title">Title</label>
						<Input
							id="deal-title"
							type="text"
							value={editForm.title || ""}
							onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
							placeholder="Deal name"
							colorScheme="light"
						/>
					</div>

					<div className="modal-form-field">
						<label htmlFor="deal-type">Deal Type</label>
						<Input
							id="deal-type"
							type="text"
							value={editForm.deal_type || ""}
							onChange={(e) => setEditForm({ ...editForm, deal_type: e.target.value })}
							placeholder="e.g. New Business, Expansion"
							colorScheme="light"
						/>
					</div>
					<div className="modal-form-field">
						<label htmlFor="deal-acquisition-source">Acquisition Source</label>
						<Input
							type="dropdown"
							id="deal-acquisition-source"
							value={getAcquisitionSourceLabel(editForm.acquisition_source)}
							allowCustom={false}
							colorScheme="light"
							options={[...ACQUISITION_SOURCES, { value: "unknown", label: "Unknown" }]}
							onOptionSelect={(option) => setEditForm({ ...editForm, acquisition_source: option.value })}
						/>
					</div>
					<div className="modal-form-field">
						<label htmlFor="deal-primary-offer">Primary Offer</label>
						<Input
							type="dropdown"
							id="deal-primary-offer"
							value={getPrimaryOfferLabel(editForm.primary_offer)}
							allowCustom={false}
							colorScheme="light"
							options={PRIMARY_OFFERS}
							onOptionSelect={(option) => setEditForm({ ...editForm, primary_offer: option.value })}
						/>
					</div>
					<div className="modal-form-field short-form-field">
						<label htmlFor="deal-value">Deal Value</label>
						<Input
							id="deal-value"
							type="number"
							value={(editForm.amount_cents || 0) / 100}
							onChange={(e) =>
								setEditForm({
									...editForm,
									amount_cents: Math.round(Number(e.target.value || 0) * 100),
								})
							}
							placeholder="0"
							colorScheme="light"
						/>
					</div>
					<div className="modal-form-field short-form-field">
						<label htmlFor="deal-currency">Currency</label>
						<Input
							type="dropdown"
							id="deal-currency"
							name="deal-currency"
							placeholder="Select currency"
							value={selectedCurrencyLabel}
							allowCustom={false}
							colorScheme="light"
							ariaLabel="Currency options"
							renderInPortal={true}
							options={CURRENCY_OPTIONS}
							onOptionSelect={(option) => setEditForm({ ...editForm, currency: option.value })}
						/>
					</div>



					<div className="modal-form-field wide-form-field">
						<div className="deal-products-label-row">
							<label htmlFor="deal-product-search">Products</label>
							<div className="deal-product-pills" role="status" aria-live="polite">
								{productsLoading ? <span className="deal-product-pill deal-product-pill--empty">Loading...</span> : null}
								{!productsLoading && productsError ? <span className="deal-product-pill deal-product-pill--empty">Error</span> : null}
								{!productsLoading && !productsError && dealProducts.length === 0 ? <span className="deal-product-pill deal-product-pill--empty">None selected</span> : null}
								{!productsLoading && !productsError && dealProducts.map((product) => (
									<span key={String(product.id || product.product_id)} className="deal-product-pill">
										<span className="deal-product-pill-text">{product.product_name || "Unnamed product"}</span>
										{dealProducts.length >= 2 && (
											<button
												type="button"
												className="deal-product-pill-remove"
												onClick={() => handleRemoveProduct(product)}
												disabled={removingProductId === product.id}
												aria-label={`Remove ${product.product_name || "product"}`}
											>
												<LuX />
											</button>
										)}
									</span>
								))}
							</div>
						</div>
						<Input
							type="dropdown"
							id="deal-product-search"
							name="deal-product-search"
							placeholder={selectableProducts.length === 0 ? "All products added" : "Search product to add..."}
							value={productQuery}
							onChange={(e) => {
								setProductQuery(e.target.value);
								setAddProductError("");
							}}
							allowCustom={true}
							colorScheme="light"
							ariaLabel="Product suggestions"
							renderInPortal={true}
							disabled={pickerLoading || isAddingProduct || selectableProducts.length === 0}
						>
							{({ closeMenu }) => (
								<>
									{pickerLoading ? <div className="dropdown-menu-empty">Loading products...</div> : null}
									{!pickerLoading && pickerError ? <div className="dropdown-menu-empty">{pickerError}</div> : null}
									{!pickerLoading && !pickerError && filteredSelectableProducts.map((product) => (
										<button
											key={String(product.id)}
											type="button"
											className="dropdown-menu-item"
											onClick={async () => {
												closeMenu();
												await handleSelectProduct(product);
											}}
										>
											{product.name}
										</button>
									))}
									{!pickerLoading && !pickerError && filteredSelectableProducts.length === 0 ? (
										<div className="dropdown-menu-empty">
											{selectableProducts.length === 0 ? "No more products available to add" : "No products found"}
										</div>
									) : null}
								</>
							)}
						</Input>
						{addProductError ? <div className="lookup-empty">{addProductError}</div> : null}
					</div>

					<div className="modal-form-field wide-form-field">
						<label>Stage history</label>
						<div className="deal-stage-history">
							{stageHistoryLoading ? <span className="modal-form-field-note">Loading history...</span> : null}
							{!stageHistoryLoading && stageHistoryError ? <span className="lookup-empty">{stageHistoryError}</span> : null}
							{!stageHistoryLoading && !stageHistoryError && stageHistory.length === 0 ? <span className="modal-form-field-note">No stage changes recorded.</span> : null}
							{!stageHistoryLoading && !stageHistoryError && stageHistory.map((item) => (
								<div key={item.id} className="deal-stage-history-row">
									<strong>{item.previous_stage} to {item.new_stage}</strong>
									<span>{item.changed_by_name || "Unknown user"} · {item.created_at ? new Date(item.created_at).toLocaleString() : "-"}</span>
								</div>
							))}
						</div>
					</div>
				</div>
			</div>
				</Modal>
			)}

			{isCreateMeetingOpen && (
				<CreateMeetingModal
					isOpen={isCreateMeetingOpen}
					onClose={() => setIsCreateMeetingOpen(false)}
					onSuccess={() => {}}
					dealId={dealId}
					accountId={deal?.account_id}
					accountName={deal?.account}
					contactId={deal?.contact_id}
					contactEmail={deal?.contact_email}
					contactName={deal?.contact_name}
				/>
			)}

			{isConversationsOpen && (
				<DealConversationsModal
					isOpen={isConversationsOpen}
					onClose={() => setIsConversationsOpen(false)}
					dealId={dealId}
					contactId={deal?.contact_id}
					contactName={deal?.contact_name}
				/>
			)}

			
		</>
	);
}

export default DealDetailsModal;
